import { Link } from 'react-router-dom';
import { Button, Card, Icon } from '../components/ui';
import { usePageMeta } from '../lib/seo';
import { LogoMark } from '../components/Logo';
import { tr } from '../lib/i18n';

const STEPS = [
  {
    icon: 'settings' as const,
    title: 'Configure sua agenda',
    text: 'Defina expediente, folgas, serviços, preços e duração. A AgendaPro calcula os horários disponíveis automaticamente.'
  },
  {
    icon: 'user' as const,
    title: 'Divulgue seu perfil',
    text: 'Você recebe um link público com seus serviços, foto e políticas. Basta compartilhar no WhatsApp ou nas redes.'
  },
  {
    icon: 'calendar' as const,
    title: 'Receba reservas',
    text: 'O cliente escolhe serviço, data e horário e confirma sem precisar de cadastro. Você aprova ou recebe direto na agenda.'
  }
];

const PRO_BENEFITS = [
  'Agenda própria com serviços, horários e bloqueios em um só lugar.',
  'Perfil público com link próprio para compartilhar com clientes.',
  'Horários calculados a partir do seu expediente, folgas e buffers.',
  'Atalho de WhatsApp em cada agendamento, abrindo a conversa pronta.',
  'Histórico de clientes, cancelamentos e períodos bloqueados.'
];

const BIZ_BENEFITS = [
  'Controle central da agenda de todos os profissionais.',
  'Defina quem pode ver e editar cada área do painel.',
  'Relatórios de ocupação, serviços e desempenho da equipe.',
  'Ajuste políticas de cancelamento e confirmação por estabelecimento.',
  'Portfólio ampliado e marcação de serviços por profissional.'
];

const CATEGORIES = [
  'Manicure e pedicure',
  'Cabelo',
  'Nail design',
  'Barbearia',
  'Estética',
  'Design de sobrancelhas',
  'Lash design',
  'Maquiagem',
  'Depilação',
  'Bem-estar e spa',
  'Tatuagem e piercing',
  'Banho e tosa',
  'Fisioterapia',
  'Massoterapia',
  'Personal trainer',
  'Podologia',
  'Odontologia',
  'Aulas e consultoria'
];

const FAQ = [
  {
    q: 'Preciso pagar para começar?',
    a: 'Não. Você começa com um teste gratuito de 7 dias, sem cartão. Ao fim do teste, é necessária uma assinatura paga para continuar usando.'
  },
  {
    q: 'Meus clientes precisam criar conta para agendar?',
    a: 'Não. O agendamento pelo seu link público funciona sem login — o cliente informa nome e um contato (e-mail ou telefone).'
  },
  {
    q: 'Posso aceitar mais de um profissional?',
    a: 'Depende do plano. Cada plano tem um limite de profissionais; você pode fazer upgrade quando a equipe crescer.'
  }
];

export function HomePage(): JSX.Element {
  usePageMeta(
    tr('Agenda online para beleza, saúde, bem-estar e serviços'),
    tr('A AgendaPro organiza sua agenda, publica seu perfil e recebe reservas pelo WhatsApp. Seu negócio. Sua agenda. Seu tempo.'),
    '/'
  );

  return (
    <div>
      <section className="mx-auto w-full max-w-content px-4 pb-4 pt-12">
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-card px-3 py-1 text-xs font-semibold text-muted">
              <Icon name="clock" size={14} /> {tr("Teste gratuito de 7 dias, sem cartão")}
            </span>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">
              {tr("Seu negócio. Sua agenda. Seu tempo.")}
            </h1>
            <p className="mt-4 max-w-lg text-lg text-muted">
              {tr("A AgendaPro é a plataforma de agendamento para salões, barbearias, estúdios de tatuagem e piercing, clínicas de estética e odontologia, fisioterapia, massoterapia, podologia, personal trainers, banho e tosa e aulas ou consultorias. Publique seu perfil, deixe o cliente escolher o horário e tenha a agenda organizada.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/comecar">
                <Button>{tr("Começar teste grátis")}</Button>
              </Link>
              <Link to="/busca">
                <Button variant="secondary" icon={<Icon name="search" />}>
                  {tr("Encontrar profissional")}
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-muted">
              {tr("Sem anúncios e sem taxas por reserva. Você paga apenas a assinatura do plano.")}
            </p>

          </div>
          <Card className="bg-chrome text-chrome-ink">
            <div className="flex items-center gap-3">
              <LogoMark variant="light" size={44} />
              <p className="font-display text-xl font-semibold">{tr("Exemplo de agenda")}</p>
            </div>
            <ul className="mt-5 space-y-3">
              {[
                { t: 'Manhã', slots: ['09:00', '10:30', '11:00'] },
                { t: 'Tarde', slots: ['14:00', '15:30'] }
              ].map((block) => (
                <li key={block.t}>
                  <p className="text-sm text-sand">{tr(block.t)}</p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {block.slots.map((s) => (
                      <span key={s} className="rounded-md bg-ivory/10 px-2.5 py-1 text-sm">
                        {s}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-sand/80">
              {tr("Demonstração ilustrativa. Os horários reais vêm do seu expediente, folgas e reservas.")}
            </p>
          </Card>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-14">
        <h2 className="text-center font-display text-3xl font-semibold text-ink">{tr("Como funciona")}</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {STEPS.map((step, i) => (
            <Card key={step.title} as="article">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-surface text-graphite">
                  <Icon name={step.icon} />
                </span>
                <span className="font-mono text-sm text-muted">{tr("Passo")} {i + 1}</span>
              </div>
              <h3 className="mt-3 text-lg font-semibold">{tr(step.title)}</h3>
              <p className="mt-1.5 text-sm text-muted">{tr(step.text)}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-surface py-14">
        <div className="mx-auto w-full max-w-content px-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold">
                <Icon name="user" /> {tr("Se você trabalha sozinho")}
              </h2>
              <p className="mt-1 text-sm text-muted">{tr("O plano Start inclui tudo o que está aqui.")}</p>
              <ul className="mt-4 space-y-2.5">
                {PRO_BENEFITS.map((b) => (
                  <li key={b} className="flex gap-2 text-sm text-ink">
                    <span className="mt-0.5 text-sage"><Icon name="check" /></span>
                    {tr(b)}
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold">
                <Icon name="shield" /> {tr("Se você tem uma equipe")}
              </h2>
              <p className="mt-1 text-sm text-muted">{tr("Os planos Pro e Business liberam estes recursos.")}</p>
              <ul className="mt-4 space-y-2.5">
                {BIZ_BENEFITS.map((b) => (
                  <li key={b} className="flex gap-2 text-sm text-ink">
                    <span className="mt-0.5 text-sage"><Icon name="check" /></span>
                    {tr(b)}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-14">
        <h2 className="font-display text-3xl font-semibold text-ink">{tr("Categorias atendidas")}</h2>
        <p className="mt-2 text-muted">{tr("Serviços que a AgendaPro foi feita para organizar.")}</p>
        <div className="mt-6 flex flex-wrap gap-2.5">
          {CATEGORIES.map((c) => (
            <span key={c} className="rounded-full border border-line bg-card px-4 py-2 text-sm font-medium text-ink">
              {tr(c)}
            </span>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 pb-6">
        <Card className="text-center">
          <h2 className="font-display text-2xl font-semibold">{tr("Planos simples, sem surpresas")}</h2>
          <p className="mx-auto mt-2 max-w-xl text-muted">
            {tr("Comece com 7 dias de teste gratuito. Depois do teste, o uso contínuo exige uma assinatura paga mensal, semestral ou anual. Não cobramos taxa por agendamento — você vê o preço de cada plano antes de contratar.")}
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <Link to="/precos">
              <Button variant="secondary">{tr("Ver planos e preços")}</Button>
            </Link>
            <Link to="/comecar">
              <Button>{tr("Começar teste grátis")}</Button>
            </Link>
          </div>
        </Card>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-12">
        <div className="grid items-center gap-6 md:grid-cols-[1fr_auto]">
          <div>
            <h2 className="font-display text-2xl font-semibold text-ink">{tr("Dúvidas frequentes")}</h2>
            <div className="mt-4 space-y-4">
              {FAQ.map((item) => (
                <div key={item.q} className="rounded-md border border-line bg-card p-4">
                  <p className="font-semibold text-ink">{tr(item.q)}</p>
                  <p className="mt-1 text-sm text-muted">{tr(item.a)}</p>
                </div>
              ))}
            </div>
          </div>
          <Card className="bg-chrome text-chrome-ink">
            <p className="font-display text-lg font-semibold">{tr("Pronto para organizar sua agenda?")}</p>
            <p className="mt-2 text-sm text-sand">{tr("Crie seu perfil e receba sua primeira reserva ainda hoje.")}</p>
            <Link to="/comecar" className="mt-4 inline-block">
              <Button variant="secondary" className="!border-transparent !bg-chrome-ink !text-chrome">{tr("Criar minha conta")}</Button>
            </Link>
            <Link to="/faq" className="mt-3 block text-center text-sm text-sand underline">{tr("Ver todas as dúvidas")}</Link>
          </Card>
        </div>
      </section>
    </div>
  );
}
